<template>
  <div class="proposal-details-container my-team-parent">
    <!-- Owner Change Modal -->
    <b-modal v-model="showOwnerModal" centered size="md" hide-footer>
      <div class="form-group mb-4">
        <label class="form-label">Select New Owner</label>
        <b-form-select v-model="selectedOwner" :options="ownerOptions" :disabled="loadingOwners" />
      </div>

      <div class="d-flex justify-content-end pt-1">
        <b-button variant="outline-secondary" class="mr-2" @click="showOwnerModal = false" :disabled="loadingOwners">
          Cancel
        </b-button>

        <b-button variant="primary" @click="updateOwner" :disabled="!selectedOwner || loadingOwners">
          <span v-if="loadingOwners" class="spinner-border spinner-border-sm mr-1"></span>
          {{ loadingOwners ? "Updating..." : "Update" }}
        </b-button>
      </div>
    </b-modal>
    <!-- End Owner Change Modal -->
    
    <!-- Assignees Modal -->
    <b-modal v-model="showAssigneesModal" title="Manage Assignees" centered size="lg" hide-footer
      content-class="assignee-modal" header-class="border-0" body-class="p-0">
      <div>
        <div class="form-group mb-4 pt-1">
          <div class="input-group">
            <input v-model="searchQuery" type="text" class="form-control" placeholder="Search by name" />
          </div>
        </div>

        <div class="assignee-list mb-2">
          <div v-for="user in filteredAssignees" :key="user.id" class="assignee-row">
            <b-form-checkbox v-model="selectedAssignees" :value="user.id" class="assignee-checkbox">
              <div class="assignee-content">
                <!-- Avatar -->
                <b-avatar :text="user.full_name
                    ? user.full_name.charAt(0).toUpperCase()
                    : 'U'
                  " variant="primary" size="32" class="mr-2" />

                <!-- Name & email -->
                <div class="assignee-text">
                  <div class="assignee-name">
                    {{ user.full_name || 'Unnamed User' }}
                  </div>
                  <small class="text-muted">
                    {{ user.email ? user.email : '-'}}
                  </small>
                </div>
              </div>
            </b-form-checkbox>
          </div>

          <div v-if="filteredAssignees.length === 0" class="text-center p-3 text-muted">
            No users found
          </div>
        </div>

        <div v-if="selectedAssignees.length > 0" class="selected-assignees-section mb-4">
          <h6 class="mb-2">
            Selected Assignees ({{ selectedAssignees.length }})
          </h6>
          <div class="selected-assignees d-flex flex-wrap">
            <b-badge v-for="id in selectedAssignees" :key="'selected-' + id" variant="light-primary"
              class="mr-2 mb-2 badge-padding d-flex align-items-center">
              {{
                (getUserById(id) && getUserById(id).full_name) || "Unknown User"
              }}
              <feather-icon icon="XIcon" size="14" class="ml-1 cursor-pointer" @click="removeAssignee(id)" />
            </b-badge>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-end pt-1 mt-4">
          <b-button variant="outline-secondary" class="mr-2 px-4" @click="showAssigneesModal = false"
            :disabled="loadingAssignees">
            Cancel
          </b-button>
          <b-button variant="primary" class="px-4" @click="updateAssignees"
            :disabled="loadingAssignees || selectedAssignees.length === 0">
            <span v-if="loadingAssignees" class="spinner-border spinner-border-sm mr-1" role="status"></span>
            {{ loadingAssignees ? "Updating..." : "Update" }}
          </b-button>
        </div>
      </div>
    </b-modal>
    <!-- End Assignees Modal -->
    
    <!-- Proposal Details -->
    <div class="card-filter-section mb-50 mt-0 d-flex align-items-center justify-content-between">
      <p class="task-timesheet m-0">Proposal Details</p>
      <button class="back-button btn btn-secondary mr-2 ml-auto" @click="back()">
        <feather-icon icon="ArrowLeftIcon" width="18" height="18" />
        <span>Back to Proposal</span>
      </button>
      <Help v-show="$store.state.app.userManualList" code="ProposalDetails" />
    </div>
    <b-card class="proposal-card">
      <!-- Header with back button and title -->
      <div class="d-flex justify-content-between align-items-center p-1">
        <div class="d-flex align-items-center"></div>
        <b-badge :variant="getStatusVariant(proposal.status)" class="status-badge">
          {{ getStatusText(proposal.status) }}
        </b-badge>
      </div>

      <!-- Main Content -->
      <div class="proposal-content">
        <b-row>
          <!-- LEFT CARD : PROPOSAL DETAILS -->
          <b-col md="8">
            <b-card class="inner-card">
              <!-- ROW 1 : DETAILS + OWNER/ASSIGNEES -->
              <b-row>
                <!-- PROPOSAL DETAILS -->
                <b-col md="8">
                  <!-- details content -->
                  <b-row>
                    <b-col md="6" class="mb-2">
                      <div class="detail-item">
                        <span class="detail-label">Project Title</span>
                        <span class="detail-value">{{
                          proposal.project_title || "-"
                          }}</span>
                      </div>
                    </b-col>

                    <b-col md="6" class="mb-2" v-if="canViewFullDetails">
                      <div class="detail-item">
                        <span class="detail-label">Client Company</span>
                        <span class="detail-value">{{
                          proposal.client_company_name || "-"
                          }}</span>
                      </div>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col md="6" class="mb-2" v-if="canViewFullDetails">
                      <div class="detail-item">
                        <span class="detail-label">CRM Lead Link</span>
                        <a v-if="proposal.crm_lead_link" :href="proposal.crm_lead_link" target="_blank"
                          class="detail-value text-primary">
                          View Lead
                          <feather-icon icon="ExternalLinkIcon" size="14" />
                        </a>
                        <span v-else>-</span>
                      </div>
                    </b-col>

                    <b-col md="6" class="mb-2">
                      <div class="detail-item">
                        <span class="detail-label">Proposal Type</span>
                        <span class="detail-value">{{
                          proposal.proposal_type || "-"
                          }}</span>
                      </div>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col md="12" class="mb-2">
                      <div class="detail-item">
                        <span class="detail-label">Description</span>
                        <span class="detail-value">{{
                          proposal.description || "-"
                          }}</span>
                      </div>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col md="6" class="mb-2" v-if="canViewFullDetails">
                      <div class="detail-item">
                        <span class="detail-label">Proposal Size</span>
                        <span class="detail-value">{{
                          proposal.project_size || "-"
                          }}</span>
                      </div>
                    </b-col>

                    <b-col md="6" class="mb-2">
                      <div class="detail-item">
                        <span class="detail-label">Expected Tech Stack</span>
                        <span class="detail-value">{{
                          proposal.expected_tech_stack || "-"
                          }}</span>
                      </div>
                    </b-col>
                  </b-row>

                  <b-row>
                    <b-col md="6" class="mb-2" v-if="canViewFullDetails">
                      <div class="detail-item">
                        <span class="detail-label">Lead Qualification Score</span>
                        <span class="detail-value">
                          {{ proposal.lead_qualification_score || "-" }}
                        </span>
                      </div>
                    </b-col>

                    <b-col md="6" class="mb-2" v-if="canViewFullDetails">
                      <div class="detail-item">
                        <span class="detail-label">Date Created</span>
                        <span class="detail-value">
                          {{ formatDate(proposal.created_at) || "-" }}
                        </span>
                      </div>
                    </b-col>
                  </b-row>
                </b-col>

                <!-- OWNER + ASSIGNEES -->
                <b-col md="4">
                  <!-- owner + assignees content -->
                  <template v-if="canViewFullDetails">
                    <div class="mb-3">
                      <div class="d-flex justify-content-between align-items-center mb-1">
                        <span class="detail-label" style="font-weight: bold; font-size: 1.1rem;">Owner</span>
                        <b-button v-if="isSuperAdmin && proposal.deleted_at === null" variant="link" class="p-0 text-primary"
                          @click="openOwnerModal">
                          <feather-icon icon="Edit2Icon" size="14" />
                        </b-button>
                      </div>

                      <div class="d-flex align-items-center owner-row">
                        <b-avatar :src="proposal.owner && proposal.owner.picture
                            ? proposal.owner.picture
                            : null
                          " :text="getOwnerInitials" size="32" variant="primary" class="mr-2" />
                        <span class="detail-value">
                          {{
                            proposal.owner && proposal.owner.full_name
                              ? proposal.owner.full_name
                              : "-"
                          }}
                        </span>
                      </div>
                    </div>
                  </template>

                  <div>
                    <div class="d-flex justify-content-between align-items-center mb-2" >
                      <span class="detail-label" style="font-weight: bold; font-size: 1.1rem;">
                        Assignees ({{ assigneeCount }})
                      </span>
                      <b-button v-if="isSuperAdmin && proposal.deleted_at === null" size="sm" variant="outline-primary"
                        @click="openAssigneesModal" >
                        Edit
                      </b-button>
                    </div>

                    <div v-if="hasAssignees" class="assignees-container">
                      <div v-for="item in proposal.proposal_assignees" :key="item.id"
                        class="assignee-row d-flex align-items-center justify-content-between">
                        <div class="d-flex align-items-center">
                          <b-avatar :src="item.user && item.user.picture
                              ? item.user.picture
                              : null
                            " :text="getInitials(item.user)" size="28" variant="primary" class="mr-2" />
                          <div>
                            <div class="assignee-name">
                              {{
                                item.user && item.user.full_name
                                  ? item.user.full_name
                                  : "N/A"
                              }}
                            </div>
                            <small class="text-muted">Assigned</small>
                          </div>
                        </div>

                        <feather-icon icon="Trash2Icon" v-if="isSuperAdmin && proposal.deleted_at === null" size="14"
                          class="text-muted cursor-pointer assignee-remove" @click="deleteAssignee(item)" />
                      </div>
                    </div>

                    <div v-else class="text-muted">No assignees</div>
                  </div>
                </b-col>
              </b-row>             
            <b-row class="mt-3">
              <!-- ATTACHMENTS (UNCHANGED LOGIC) -->
              <b-col md="6">
                <b-card class="inner-card h-100">
                  <div class="d-flex align-items-center mb-2">
                    <h5 class="section-title mb-0">Attachments</h5>
                    <span class="badge badge-light-primary ml-2">
                      {{
                        proposal.proposal_attachments
                          ? proposal.proposal_attachments.length
                          : 0
                      }}
                    </span>
                  </div>

                  <b-card
                    v-if="
                      proposal.proposal_attachments &&
                      proposal.proposal_attachments.length
                    "
                    class="attachment-list"
                  >
                    <div class="attachments-scroll custom-scroll">
                      <div
                        v-for="(attachment, index) in proposal.proposal_attachments"
                        :key="index"
                        class="attachment-item"
                      >
                        <div class="d-flex justify-content-between align-items-center">
                          <div class="d-flex align-items-center">
                            <div class="file-icon mr-2">
                              <feather-icon
                                :icon="getFileIcon(attachment.type)"
                                size="20"
                              />
                            </div>

                            <div class="text-truncate" style="max-width: 180px">
                              <div
                                class="file-name text-truncate"
                                v-b-tooltip.hover.top="attachment.name"
                              >
                                {{ attachment.name }}
                              </div>
                              <div class="file-details text-muted small">
                                {{ getFileType(attachment.name) }}
                              </div>
                            </div>
                          </div>

                          <b-button
                            variant="outline-primary"
                            size="sm"
                            @click="downloadFile(attachment)"
                          >
                            <feather-icon
                              icon="DownloadIcon"
                              size="16"
                              class="mr-50"
                            />
                            Download
                          </b-button>
                        </div>
                      </div>
                    </div>
                  </b-card>

                  <div v-else class="no-attachments text-muted text-center py-3">
                    No attachments available
                  </div>
                </b-card>
              </b-col>

              <!-- ACTIVITY / STATUS TIMELINE -->
              <b-col md="6">
                <b-card class="inner-card h-100">
                  <h5 class="section-title mb-3">Activity</h5>

                  <div class="timeline-scroll custom-scroll">
                    <div
                      v-for="(item, index) in activity_logs"
                      :key="index"
                      class="timeline-item"
                    >
                      <!-- DOT + LINE -->
                      <div class="timeline-marker">
                        <div
                          class="timeline-dot-wrapper"
                          :class="item.description.toLowerCase().replace(/\s+/g, '_')"
                        >
                          <div class="timeline-dot-inner"></div>
                        </div>

                        <!-- vertical line (not for last item) -->
                        <div
                          v-if="index !== activity_logs.length - 1"
                          class="timeline-line"
                        ></div>
                      </div>

                      <!-- CONTENT -->
                      <div class="timeline-content">
                        <div class="d-flex justify-content-between align-items-center">
                          <span
                            class="status-badge"
                            :class="item.description.toLowerCase().replace(/\s+/g, '_')"
                          >
                            {{ item.description }}
                          </span>

                          <!-- TIME DIFF PILL -->
                          <span
                            v-if="item.diffText"
                            class="time-diff-pill ml-1"
                            :class="[
                              item.diffType === 'short' ? 'pill-short' : 'pill-long',
                              { danger: item.isDelayed }
                            ]"
                          >
                            <feather-icon
                              :icon="item.diffType === 'long' ? 'CalendarIcon' : 'ClockIcon'"
                              size="14"
                              class="mr-25"
                            />
                            {{ item.diffText }} 
                            <span v-if="item.isDelayed">!</span>
                          </span>

                        </div>

                        <div class="activity-meta">
                          <feather-icon icon="UserIcon" size="16" class="activity-user-icon" />
                          <span class="activity-user-name">
                            {{ item.causer.full_name }}
                          </span>
                          <span class="activity-separator">•</span>
                          <span class="activity-time">
                            {{ formatDateTime(item.created_at) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                </b-card>
              </b-col>
            </b-row>

            </b-card>
          </b-col>

          <b-col md="4">
            <b-card class="inner-card ownership-card">
              <div class="comments-section mt-0">
                <div class="d-flex align-items-center mb-2">
                  <h5 class="section-title mb-0">Threads</h5>
                </div>

                <b-card class="comments-card">
                  <!-- Messages Container -->
                  <div class="messages-container" ref="messagesContainer">
                    <template v-if="proposal.trails && proposal.trails.length > 0">
                    <div
                      v-for="(message, index) in proposal.trails"
                      :key="index"
                      :class="[
                        'message-row',
                        message.created_by.id === $store.state.app.userInfo.id ? 'own-message' : 'other-message'
                      ]"
                    >
                      <div
                        :class="[
                          'message-bubble',
                          message.created_by.id === $store.state.app.userInfo.id ? 'sent' : 'received'
                        ]"
                      >
                        <!-- <div class="message-content">
                          {{ message.message }}
                        </div> -->
                        <div
                          class="message-content"
                          v-html="formatMessage(message.message)"
                        ></div>

                        <div class="message-meta">
                          <span
                            class="sender-name"
                            v-if="message.created_by.id !== $store.state.app.userInfo.id"
                          >
                            {{ message.created_by.full_name || 'User' }}
                          </span>

                          <span class="message-time">
                            {{ formatDateTime(message.created_at) }}
                          </span>
                        </div>
                      </div>
                    </div>
                    </template>
                    <div v-else class="no-messages text-muted text-center py-3">
                      No messages yet. Start the conversation!
                    </div>
                  </div>

                  <!-- Message Input -->
                  <div class="message-input-container" v-if="proposal.deleted_at === null">
                    <b-form @submit.prevent="sendMessage" class="d-flex">
                      <b-form-input
                        v-model="newMessage"
                        placeholder="Type your message..."
                        class="flex-grow-1"
                        :disabled="isSendingMessage"
                      ></b-form-input>
                      <button
                        v-b-tooltip.hover.top
                        title="Send message"
                        class="text-capitalize proposal-review-btn"
                        :disabled="!newMessage.trim() || isSendingMessage"
                        @click="sendMessage"
                      >
                        <b-spinner
                          small
                          v-if="isSendingMessage"
                          class="mr-0"
                        />

                        <feather-icon
                          v-else
                          icon="SendIcon"
                          role="button"
                          class="proposal-text-light-icon proposal-icon-hover"
                          size="22"
                        />
                      </button>
                    </b-form>
                  </div>
                </b-card>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </div>

      <!-- Status Update Buttons -->
      <div class="action-buttons mt-4 d-flex justify-content-end gap-2" v-if="proposal.deleted_at === null">
        <!-- Assign Button - Only show for SA when status is Open -->
        <b-button v-if="proposal.status === 'O' && isSuperAdmin" variant="primary" @click="updateStatus('A')">
          Mark as Assigned
        </b-button>

        <!-- In Progress Button - Show when status is Assigned -->
        <b-button v-if="proposal.status === 'A' && (isManageDesignationUser || isSuperAdmin)" variant="primary" @click="updateStatus('IP')">
          Mark as In Progress
        </b-button>

        <!-- In Review Button - Show when status is In Progress -->
        <b-button v-if="proposal.status === 'IP' && (isManageDesignationUser || isSuperAdmin)" variant="primary" @click="updateStatus('IR')">
          Submit
        </b-button>

        <!-- Complete Button - Only show for SA when status is In Review -->
        <div v-if="proposal.status === 'IR' && isSuperAdmin">
          <b-button
            variant="success"
            class="mr-1"
            @click="updateStatus('C')"
          >
            Mark as Completed
          </b-button>

          <b-button
            variant="warning"
            @click="updateStatus('IP')"
          >
            Re Open
          </b-button>
        </div>
      </div>
    </b-card>
    <!-- End Proposal Details -->
  </div>
</template>

<script>
import proposalDetailsMixin from "@/mixin/proposal/proposalDetailsMixin.js";

export default {
  name: "ProposalDetails",
  mixins: [proposalDetailsMixin],
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-sweetalert.scss";
@import "./../../assets/scss/component-css/masterSettingFilter.scss";
@import "./../../assets/scss/component-css/proposalDetails.scss";
</style>
